import { WorkflowId } from "@sp-crm/core";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import {
    AdvancedSearchEntityType,
    CustomListKey,
    useCreateWorkflowMutation,
    useGetWorkflowsQuery,
    WorkflowStatus,
} from "generated/graphql";
import { useTaskNouns } from "hooks/use-task-nouns";
import React from "react";
import { Redirect, useRouteMatch } from "react-router-dom";
import { navigate } from "store/actions";
import { useCustomList, useWorkflowEntityTypes } from "store/selectors/bridge";
import { useCurrentUserId, useDefaultRegionVisibility } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { workflowEventProperties } from "./builder-analytics";
import { workflowsForEntityTypes } from "./builder-helper";
import {
    BlueprintContext,
    composeNewWorkflow,
    defaultBlueprint,
} from "./workflow-blueprint";
import { WorkflowCardList } from "./workflow-card-list";
import { WorkflowWizard } from "./workflow-wizard";

export const WorkflowBuilderPage: React.FC = () => {
    const { params } = useRouteMatch<{ workflowId?: WorkflowId }>();
    const workflows = useGetWorkflowsQuery();
    const workflowEntityTypes = useWorkflowEntityTypes();
    const nouns = useTaskNouns();
    const currentUserId = useCurrentUserId();
    const defaultRegionVisibility = useDefaultRegionVisibility();
    const createWorkflowMutation = useCreateWorkflowMutation();
    const { customList: clientStatusCustomList } = useCustomList(
        CustomListKey.ClientStatus,
    );
    const { customList: homeCareLeadStatusCustomList } = useCustomList(
        CustomListKey.HomeCareLeadStatus,
    );
    const [searchText, setSearchText] = React.useState("");
    const [entityTypeFilter, setEntityTypeFilter] = React.useState<
        AdvancedSearchEntityType | "all"
    >("all");
    const [statusFilter, setStatusFilter] = React.useState<WorkflowStatus | "all">("all");

    const handleRefetch = React.useCallback(async () => {
        await workflows.refetch();
    }, [workflows]);

    const blueprintContext = React.useMemo<BlueprintContext>(
        () => ({
            nouns,
            clientStatusCustomList: clientStatusCustomList ?? null,
            homeCareLeadStatusCustomList: homeCareLeadStatusCustomList ?? null,
        }),
        [nouns, clientStatusCustomList, homeCareLeadStatusCustomList],
    );

    const createBlueprint = React.useMemo(
        () => defaultBlueprint(workflowEntityTypes, blueprintContext),
        [workflowEntityTypes, blueprintContext],
    );

    const visibleWorkflows = React.useMemo(
        () =>
            workflowsForEntityTypes(
                workflowEntityTypes,
                workflows.data?.getWorkflows ?? [],
            ),
        [workflowEntityTypes, workflows.data?.getWorkflows],
    );

    const createInFlight = React.useRef(false);

    const handleCreate = React.useCallback(async () => {
        if (createInFlight.current || !createBlueprint) {
            return;
        }
        const existingNames = (workflows.data?.getWorkflows ?? []).map(
            workflow => workflow.name,
        );
        const payload = composeNewWorkflow(createBlueprint, blueprintContext, {
            currentUserId,
            regionVisibility: defaultRegionVisibility,
            existingNames,
        });
        if (!payload) {
            return;
        }
        createInFlight.current = true;
        try {
            const result = await createWorkflowMutation.mutateAsync({
                workflow: payload,
            });
            handleEvent("workflow-builder-create", {
                ...workflowEventProperties(
                    {
                        id: result.createWorkflow.id,
                        attachTo: createBlueprint.entityType,
                        status: WorkflowStatus.Draft,
                    },
                    createBlueprint,
                ),
                existingWorkflowCount: visibleWorkflows.length,
            });
            await workflows.refetch();
            navigate(`/settings/workflow/${result.createWorkflow.id}`);
        } finally {
            createInFlight.current = false;
        }
    }, [
        createBlueprint,
        blueprintContext,
        currentUserId,
        defaultRegionVisibility,
        createWorkflowMutation,
        workflows,
        visibleWorkflows,
    ]);

    const canCreate = !!createBlueprint && !createWorkflowMutation.isLoading;

    return (
        <QueryRenderer name="WorkflowBuilderPage" query={workflows}>
            {data => {
                const selectedWorkflow = params?.workflowId
                    ? visibleWorkflows.find(w => w.id === params.workflowId) || null
                    : null;

                if (params?.workflowId && !selectedWorkflow) {
                    return <Redirect to="/settings/workflow" />;
                }

                if (selectedWorkflow) {
                    return (
                        <WorkflowWizard
                            key={selectedWorkflow.id}
                            workflow={selectedWorkflow}
                            workflowSettings={data.getWorkflowSettings}
                            refetch={handleRefetch}
                        />
                    );
                }

                return (
                    <WorkflowCardList
                        workflows={visibleWorkflows}
                        refreshing={workflows.isFetching}
                        onCreate={handleCreate}
                        createEnabled={canCreate}
                        searchText={searchText}
                        onSearchTextChange={setSearchText}
                        entityTypeFilter={entityTypeFilter}
                        onEntityTypeFilterChange={setEntityTypeFilter}
                        statusFilter={statusFilter}
                        onStatusFilterChange={setStatusFilter}
                    />
                );
            }}
        </QueryRenderer>
    );
};
